// 🔧 passport_defaults.js
// ✅ Purpose: Provides adaptive Passport demo content
// ✅ Features:
// - Builds Core Passport stops from event features
// - Adds optional stops when add-on engines exist in DemoContext
// - Builds rewards, achievements, and sample progress
// - Returns plain JavaScript objects only
// - Fully block-commented for clarity and future devs

import { getIndustryTemplate } from "../../../industries";
import { getPowerUpProfile } from "../../../industries/power_up_profiles";

const buildDemoAttendees = (industryTemplate) =>
  getPowerUpProfile(industryTemplate).passportPeople.map((name, index) => ({
    uid: `demo-${industryTemplate}-attendee-${String(index + 1).padStart(3, "0")}`,
    name,
    email: `${name.toLowerCase().replace(/[^a-z0-9]+/g, ".")}@example.com`,
  }));

export const buildPassportDefaults = ({
  industryTemplate = "car_show",
  demoSize = "medium",
  demoContext,
} = {}) => {
  const stops = [
    ...buildCoreStops({ industryTemplate }),
    ...buildSponsorStops({ demoContext }),
    ...buildVendorStops({ demoContext }),
    ...buildAuctionStops({ demoContext }),
    ...buildStoreStops({ demoContext }),
    ...buildTicketStops({ demoContext }),
  ].map((stop, index) => ({
    ...stop,
    displayOrder: (index + 1) * 10,
    active: true,
    demoSeed: true,
  }));

  const rewards = buildRewards({ demoSize, industryTemplate });
  const achievements = buildAchievements({ stops });
  const progress = buildProgress({
    stops,
    rewards,
    attendees: buildDemoAttendees(industryTemplate),
  });

  return {
    stops,
    rewards,
    achievements,
    progress,
  };
};

const buildCoreStops = ({ industryTemplate }) => {
  const industry = getIndustryTemplate(industryTemplate);
  const entryLabel = String(industry?.entryLabel || "entry").toLowerCase();
  const voterLabel = String(industry?.voterLabel || "voter").toLowerCase();

  return [
  {
    localKey: "welcome-stop",
    title: "Start Your Event Passport",
    description: "Check in and start your event engagement journey.",
    type: "core",
    module: "event",
    points: 10,
    qrCodeLabel: "WELCOME",
    completionAction: "scan_qr",
  },
  {
    localKey: "vote-stop",
    title: "Cast a Vote",
    description: `Participate as a ${voterLabel} and vote for a favorite ${entryLabel}.`,
    type: "core",
    module: "voting",
    points: 25,
    qrCodeLabel: "VOTE",
    completionAction: "vote_submitted",
  },
  {
    localKey: "map-stop",
    title: "Use the Event Map",
    description: "Find an important event location using the event map.",
    type: "core",
    module: "map",
    points: 10,
    qrCodeLabel: "MAP",
    completionAction: "view_map",
  },
  {
    localKey: "schedule-stop",
    title: "Check the Schedule",
    description: "Review the event schedule and find an activity to attend.",
    type: "core",
    module: "schedule",
    points: 10,
    qrCodeLabel: "SCHEDULE",
    completionAction: "view_schedule",
  },
  {
    localKey: "volunteer-stop",
    title: "Visit Volunteer Central",
    description: "Learn how volunteers support this event.",
    type: "core",
    module: "volunteers",
    points: 15,
    qrCodeLabel: "VOLUNTEER",
    completionAction: "scan_qr",
  },
  ];
};

const buildSponsorStops = ({ demoContext }) => {
  const sponsors = demoContext?.sponsors?.sponsors || [];

  return sponsors.slice(0, 3).map((sponsor, index) => ({
    localKey: `sponsor-stop-${index + 1}`,
    title: `Visit ${sponsor.name || "Sponsor Booth"}`,
    description: "Scan this sponsor stop to earn Passport points.",
    type: "addon",
    module: "sponsors",
    relatedName: sponsor.name || "",
    points: 20,
    qrCodeLabel: `SPONSOR-${index + 1}`,
    completionAction: "scan_qr",
  }));
};

const buildVendorStops = ({ demoContext }) => {
  const vendors = demoContext?.vendors?.vendors || [];

  return vendors.slice(0, 3).map((vendor, index) => ({
    localKey: `vendor-stop-${index + 1}`,
    title: `Visit ${vendor.name || "Vendor Booth"}`,
    description: "Find this vendor and scan the Passport stop.",
    type: "addon",
    module: "vendors",
    relatedName: vendor.name || "",
    points: 20,
    qrCodeLabel: `VENDOR-${index + 1}`,
    completionAction: "scan_qr",
  }));
};

const buildAuctionStops = ({ demoContext }) => {
  const auctionItems = demoContext?.auction?.items || [];

  if (auctionItems.length === 0) return [];

  return [
    {
      localKey: "auction-stop",
      title: "Explore the Silent Auction",
      description: "View auction items and find something worth bidding on.",
      type: "addon",
      module: "auction",
      points: 20,
      qrCodeLabel: "AUCTION",
      completionAction: "auction_viewed",
    },
  ];
};

const buildStoreStops = ({ demoContext }) => {
  const products = demoContext?.store?.products || [];

  if (products.length === 0) return [];

  return [
    {
      localKey: "store-stop",
      title: "Visit the Event Merch Store",
      description: "Browse featured event merchandise.",
      type: "addon",
      module: "store",
      points: 15,
      qrCodeLabel: "STORE",
      completionAction: "view_store",
    },
  ];
};

const buildTicketStops = ({ demoContext }) => {
  const tickets = demoContext?.tickets?.tickets || [];

  if (tickets.length === 0) return [];

  return [
    {
      localKey: "ticket-stop",
      title: "Complete Ticket Check-in",
      description: "Check in with a ticket to begin your event experience.",
      type: "addon",
      module: "tickets",
      points: 15,
      qrCodeLabel: "TICKET",
      completionAction: "ticket_checked_in",
    },
  ];
};

const buildRewards = ({ demoSize, industryTemplate }) => {
  const industryLabel =
    getIndustryTemplate(industryTemplate)?.label || "Event";
  const baseRewards = [
    {
      localKey: "starter-reward",
      title: "Passport Starter",
      description: "Awarded for completing your first Passport stop.",
      requiredPoints: 10,
      rewardType: "badge",
    },
    {
      localKey: "explorer-reward",
      title: `${industryLabel} Explorer`,
      description: "Awarded for exploring multiple event areas.",
      requiredPoints: 50,
      rewardType: "badge",
    },
    {
      localKey: "champion-reward",
      title: "Engagement Champion",
      description: "Awarded for completing a high-value Passport journey.",
      requiredPoints: demoSize === "small" ? 90 : 125,
      rewardType: "badge",
    },
  ];

  return baseRewards.map((reward, index) => ({
    ...reward,
    displayOrder: (index + 1) * 10,
    active: true,
    demoSeed: true,
  }));
};

const buildAchievements = ({ stops }) => [
  {
    localKey: "first-scan",
    title: "First Scan",
    description: "Complete your first Passport stop.",
    requiredStopCount: 1,
    pointsBonus: 5,
    active: true,
    demoSeed: true,
  },
  {
    localKey: "halfway-there",
    title: "Halfway There",
    description: "Complete half of the available Passport stops.",
    requiredStopCount: Math.max(2, Math.ceil(stops.length / 2)),
    pointsBonus: 15,
    active: true,
    demoSeed: true,
  },
  {
    localKey: "passport-complete",
    title: "Passport Complete",
    description: "Complete every available Passport stop.",
    requiredStopCount: stops.length,
    pointsBonus: 30,
    active: true,
    demoSeed: true,
  },
];

const buildProgress = ({ stops, rewards, attendees = [] }) => {
  return attendees.map((attendee, attendeeIndex) => {
    const completedStopCount = Math.min(
      stops.length,
      attendeeIndex === 0 ? stops.length : attendeeIndex + 2
    );

    const completedStops = stops
      .slice(0, completedStopCount)
      .map((stop) => ({
        stopLocalKey: stop.localKey,
        title: stop.title,
        points: stop.points,
      }));

    const totalPoints = completedStops.reduce(
      (sum, stop) => sum + Number(stop.points || 0),
      0
    );

    const earnedRewards = rewards
      .filter((reward) => totalPoints >= Number(reward.requiredPoints || 0))
      .map((reward) => reward.localKey);

    return {
      ...attendee,
      completedStops,
      totalPoints,
      earnedRewards,
      demoSeed: true,
    };
  });
};
